import { useCallback } from 'react';
import { useLanguageContext } from '@/contexts/LanguageContext';
import type { SupportedLanguage } from '@/config/i18n';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/authStore';

/**
 * Hook for managing language preferences with backend synchronization
 */
export function useLanguage() {
  const { currentLanguage, changeLanguage: changeLanguageContext, isChanging } = useLanguageContext();
  const { isAuthenticated } = useAuthStore();

  /**
   * Change language and sync with backend if user is authenticated
   */
  const changeLanguage = useCallback(
    async (language: SupportedLanguage) => {
      try {
        // Change language in i18next
        await changeLanguageContext(language);

        // Sync with backend if user is authenticated
        if (isAuthenticated) {
          try {
            await authService.updateLanguagePreference(language);
          } catch (error) {
            console.error('Failed to sync language preference with backend:', error);
            // Don't throw error here - language change succeeded locally
          }
        }
      } catch (error) {
        console.error('Failed to change language:', error);
        throw error;
      }
    },
    [changeLanguageContext, isAuthenticated]
  );

  return {
    currentLanguage,
    changeLanguage,
    isChanging,
  };
}
